import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import { callTool } from '../lib/tools'
import { errorMessage } from '../utilities/general'
import { ENDED_WINDOW_DAYS } from '../utilities/taskDeadline'
import { runTaskStop, type TaskStopOutcome } from '../utilities/computeNodePage'
import { mergeTenantTasks, type RawTask } from '../utilities/tenantTasks'
import useOrganizations from './useOrganizations'
import { readNodesAgain } from './useNode'

/**
 * Provisioned compute tasks — one row per ECS container we run for the org (router#968).
 *
 * ⚠⚠ "FOR THE ORG" MEANS THE NODE'S TENANT, `nodes.owner_org_id` (app#385, PERMISSIONS.md § 2),
 * and the network's org only for a task with no node yet. See `utilities/tenantTasks.ts`.
 *
 * ⚠ NOT kept live by realtime, and the table is not a candidate for it. `tasks` is not a member of
 * the `supabase_realtime` publication, so a `postgres_changes` channel on it would join, report
 * success and never deliver an event (app#132's shape). Freshness comes from re-fetching: the
 * usage screen fetches on open and on an org switch, and `deprovision` re-fetches after the call.
 *
 * ⚠ Loaded ON DEMAND rather than from `controller.loadState`, like `useUsage` and `useOrgMembers`
 * — nobody pays for the read until they open the screen that shows it. `controller.signOut` still
 * resets it, because the previous user's machine names must not survive a sign-in in the same tab.
 */

export type ProvisionedTask = {
  id: string
  /** Null until the container registers. For that window the name below is the only handle. */
  nodeId: string | null
  /**
   * ⚠⚠ THE LIVE NODE'S NAME WHERE THE TASK HAS ONE, NEVER `tasks.node_name` ALONE. That column is a
   * copy written at provision time and nothing updates it, so after migration `0126` made names
   * mutable a renamed machine reads under its OLD name for the rest of the task's life
   * (router#1035). The copy is the fallback, and it covers the task that has not registered yet
   * AND the one whose node this reader cannot read — see `nodeReadable`, which is what tells the
   * screen the name it is printing may be stale.
   */
  nodeName: string
  /**
   * True when the node row came back with the task.
   *
   * ⚠⚠ IT IS NOT THE SAME QUESTION AS `nodeDeleted`, AND CONFLATING THEM PUTS A DEAD LINK ON THE
   * SCREEN. A null `deleted_at` read off a row that never arrived says "live" rather than
   * "unknown". Since app#385 `useTasks` reads a task WITH a node only when this reader can read
   * the node (its tenant is the active org), so there it is always true; it stays because other
   * readers of this type (`useNodeTasks`) and older rows must not earn a link by default.
   */
  nodeReadable: boolean
  /**
   * True when the node row is soft-deleted — the usual state for an ended task.
   *
   * ⚠ MEANINGLESS UNLESS `nodeReadable`. False here means "not soft-deleted, as far as this reader
   * can see", which is not the same as "live".
   */
  nodeDeleted: boolean
  /**
   * `nodes.owner_org_id`: the node's TENANT (G-1, app#385). ⚠ MEANINGLESS UNLESS `nodeReadable`.
   * Null where the screen's read did not select it.
   *
   * The compute table does not ask a role per row in this org: `mergeTenantTasks` keeps only the
   * rows whose tenant is the active org, so the active org's role IS the role in the node's org
   * there (see `canStop` in `ProvisionedTasks`).
   *
   * ⚠ REQUIRED, not optional. A required field makes each place that builds a task (and each merge
   * that rewrites the read) set it, or tsc fails, so the row never carries a tenant silently lost.
   */
  nodeOwnerOrgId: string | null
  networkId: string
  /** Empty when the network is in an org this reader is not a member of. */
  networkName: string
  /**
   * True when the task's network is a live network of the ACTIVE org (app#385).
   *
   * ⚠ False for a node of the active org in another org's network, and for a removed network. The
   * app loads only the live networks of the active org, so that NETWORK has no page here, and the
   * table prints it without a link. The node has its page (`nodePageHref`).
   */
  networkInOrg: boolean
  cpu: number | null
  memoryMb: number | null
  createdAt: string
  completedAt: string | null
  /** ⚠ NULL means NO deadline — see `taskLifecycle`. Not "expired", not "now". */
  deadlineAt: string | null
  /**
   * Why the task ended (`tasks.stop_reason`, router migration 0191), for example `deadline`.
   * Null for an open task and for a task that ended before 0191. Absent where the screen's read
   * did not select it (`useNodeTasks` reads the reason from `get_node_tasks` instead).
   */
  stopReason?: string | null
  /**
   * The user whose compute this is: the node's `owner_user_id`, or `tasks.owner_id` for a task
   * that has no node yet (router `computeGate`). Null when the node row did not come back, so only
   * an org Owner/Admin gets the controls. Absent where the screen's read did not select it.
   */
  controlOwnerId?: string | null
}

/** One row of the compute catalogue, for naming a task's shape. */
export type ComputeClass = { name: string; cpu: number; memoryMb: number }

export interface TasksState {
  tasks: ProvisionedTask[]
  classes: ComputeClass[]
  loading: boolean
  /**
   * The task being torn down, or null.
   *
   * ⚠ ONE SLOT, AND THE SCREEN DISABLES EVERY STOP BUTTON WHILE IT IS FULL — deliberately, not as
   * an oversight. A second concurrent `deprovision` would overwrite this field, and then the FIRST
   * call's `finally` would clear it while the second is still running, leaving the table unlocked
   * mid-teardown. Allowing parallel stops means holding a set here, not relaxing the guard there.
   */
  deprovisioning: string | null
  /**
   * The task whose deadline is being moved, or null.
   *
   * ⚠ SEPARATE FROM `deprovisioning` on purpose. They are different acts with
   * different risks — one keeps a machine, the other destroys it — and one field
   * would have a pending extend disable the Stop button on every row, which is
   * the control a reader reaches for when something is going wrong.
   */
  deadlineChanging: string | null
  /**
   * The task being stopped with `task_stop`, or null (nobgp/app#337).
   *
   * ⚠ SEPARATE FROM `deprovisioning`, for the reason `deadlineChanging` is: Stop keeps
   * the node and Terminate removes it. The node page disables Terminate while this is
   * full, and Stop while `deprovisioning` is full, so the two never run on one task.
   */
  stopping: string | null
  /** The last failure, already made readable by `callTool` from the router's ToolError envelope. */
  error: string | null
  set: (state: Partial<TasksState>) => void
  /**
   * Read the org's tasks and the compute catalogue.
   *
   * ⚠ `background: true` is for a read that no screen asked for — the compute badge loader and its
   * poll (app#329). It does not set `loading`, and it does not write `error`: a failure goes to the
   * console only, and a success leaves `error` as it is. Without this, a background read on the
   * Usage screen erases a Terminate failure the reader has not seen yet, and a failed background
   * read puts a banner on a screen where the reader did nothing.
   */
  get: (options?: { background?: boolean }) => Promise<void>
  /**
   * ⚠ Resolves TRUE when `deprovision_node` succeeded, even if the re-read after it failed.
   * A caller branches on this and not on `error`: `error` is shared, and a later `get` or the
   * usage page's poll can set it for a reason that has nothing to do with this call.
   */
  deprovision: (taskId: string) => Promise<boolean>
  /**
   * Stop a running task and keep its node (nobgp/app#337).
   *
   * ⚠ Resolves to the outcome when `task_stop` succeeded, even if the re-read after it failed,
   * and to null when the router refused. A caller branches on this and not on `error`, for the
   * reason `deprovision` gives.
   */
  stop: (taskId: string) => Promise<TaskStopOutcome | null>
  /**
   * Move a running task's deadline. `hours` is an instant FROM NOW, and 0 is
   * "no time limit" — `deadlinePresets` decides what each menu entry sends, and
   * since app#347 every entry names an hour count.
   *
   * ⚠ `null` omits `max_hours` so the router applies the plan's default. NO
   * CALLER PRODUCES IT any more: the "My plan's default" entry that did was
   * removed in app#347. The arm is kept because it is the only way to ask the
   * router for its own default, not because something uses it.
   */
  setDeadline: (taskId: string, hours: number | null) => Promise<boolean>
  reset: () => void
}

const state = {
  tasks: [] as ProvisionedTask[],
  classes: [] as ComputeClass[],
  loading: false,
  deprovisioning: null,
  deadlineChanging: null,
  stopping: null,
  error: null,
}

const useTasks = create<TasksState>()((set, get) => ({
  ...state,
  set,
  get: async (options?: { background?: boolean }) => {
    const background = options?.background === true
    const orgId = useOrganizations.getState().activeOrgId
    if (!orgId) return

    // ⚠ The window is applied to `completed_at` ONLY. An open task has none, so the `is.null` arm
    // is what keeps a machine that has been running for six months on the list — filtering the
    // pair on `created_at` instead would drop exactly the forgotten containers this screen exists
    // to surface (router#968 measured 14 of them at 96.9% of all compute ever served).
    const since = new Date(Date.now() - ENDED_WINDOW_DAYS * 86400_000).toISOString()

    if (!background) set({ loading: true })

    // ⚠⚠ TWO READS: THE TWO ARMS OF THE TENANT RULE (app#385, `utilities/tenantTasks.ts`). A task
    // WITH a node belongs to `nodes.owner_org_id`; a task with NO node yet belongs to its network's
    // org. The `!inner` embed on each arm is the org scope: `tasks` RLS admits every org the caller
    // belongs to, and this screen shows one org at a time. On the node arm the networks embed is a
    // LEFT join on purpose — the node may sit in another org's network, which this reader may not
    // be able to read, and requiring it would hide the machine this org pays for.
    const columns =
      'id, node_id, node_name, network_id, cpu, memory_mb, created_at, completed_at, deadline_at, stop_reason, owner_id, '
    const period = `completed_at.is.null,completed_at.gte.${since}`
    const [nodeRes, noNodeRes, classRes] = await Promise.all([
      supabase
        .from('tasks')
        .select(columns + 'networks(name, org_id, deleted_at), nodes!inner(name, deleted_at, owner_org_id, owner_user_id)')
        .eq('nodes.owner_org_id', orgId)
        .or(period)
        .order('created_at', { ascending: false }),
      supabase
        .from('tasks')
        .select(columns + 'networks!inner(name, org_id, deleted_at)')
        .is('node_id', null)
        .eq('networks.org_id', orgId)
        .or(period)
        .order('created_at', { ascending: false }),
      // The catalogue names a task's shape. Readable by any authenticated user
      // (`compute_classes_read` is `USING (true)`), and it does not filter on `offered`: a retired
      // class still names the shape of a task that already ran.
      supabase.from('compute_classes').select('name, cpu, memory_mb'),
    ])

    // ⚠ BOTH ARMS OR NEITHER. One arm alone is a table with machines missing, and a reader takes a
    // missing row for a machine that is not running.
    const taskError = nodeRes.error ?? noNodeRes.error
    if (taskError) {
      console.warn(taskError)
      // ⚠ Same rule as the success arm below: a fetch for an org the reader has already left must
      // not put its failure on the new org's screen.
      if (useOrganizations.getState().activeOrgId !== orgId) return
      if (background) return
      return set({ loading: false, error: taskError.message })
    }
    if (classRes.error) console.warn(classRes.error)

    const raw = (res: { data: unknown }) => camelcaseKeys((res.data ?? []) as Record<string, unknown>[], { deep: true }) as unknown as RawTask[]
    const tasks = mergeTenantTasks(raw(nodeRes), raw(noNodeRes), orgId)

    const classes = camelcaseKeys(classRes.data ?? []) as ComputeClass[]

    // ⚠⚠ THE ORG IS RE-READ HERE AND NOT TRUSTED FROM THE TOP OF THE CALL. Two fetches can be in
    // flight across an org switch — the screen's own effect fires one for the new org while the
    // two-minute poll's is still out for the old — and they land in COMPLETION order. The stale
    // one landing last would fill this table with another org's machines under the new org's
    // header, each row carrying a Terminate button: `deprovision_node` is scoped to the caller's
    // orgs and NOT to the active one, so that press succeeds and destroys a container the reader
    // believed belonged to the org on screen. Dropping the result is right rather than merging it
    // — the live fetch for the current org is already on its way with the answer.
    if (useOrganizations.getState().activeOrgId !== orgId) return

    if (background) return set({ tasks, classes })
    set({ tasks, classes, loading: false, error: null })
  },

  /**
   * Stop a running task now.
   *
   * ⚠⚠ ROUTED THROUGH `callTool`, NEVER A SUPABASE WRITE. `authenticated` holds UPDATE on `tasks`
   * and the RLS policy would accept a hand-written `completed_at`, which would stamp the row and
   * leave the CONTAINER RUNNING AND BILLING — the row is a record of the provider resource, not
   * the resource. `deprovision_node` is what calls the provider.
   *
   * ⚠ It waits for the node to go offline (up to 35 s on the router), so this is deliberately not
   * optimistic: the re-fetch afterwards is what puts the true state on screen.
   */
  deprovision: async (taskId: string) => {
    set({ deprovisioning: taskId, error: null })
    let done = false
    try {
      await callTool('deprovision_node', { task_id: taskId })
      done = true
      await get().get()
      // The node row is gone from the directory, so the fleet the rest of the app renders has
      // changed under it. Nodes is subscribed to realtime and will catch the soft-delete on its
      // own; this re-read is what makes the change visible without waiting for that round trip.
      await readNodesAgain()
    } catch (error) {
      console.warn(error)
      set({ error: errorMessage(error) })
    } finally {
      set({ deprovisioning: null })
    }
    return done
  },

  /**
   * Stop a running task now and KEEP its node (nobgp/app#337).
   *
   * ⚠⚠ `task_stop`, NEVER `deprovision_node`: see `TASK_STOP_TOOL`. And never a Supabase
   * write, for the reason `deprovision` gives.
   *
   * ⚠ The router waits up to 35 s for the node to read offline, so the call is slow and
   * not optimistic. `callTool` has no timeout of its own, so the browser waits for it.
   *
   * ⚠ No `useNode` re-read: the node is NOT deleted. Its offline state reaches the
   * store through the `nodes` realtime channel.
   */
  stop: async (taskId: string) => {
    set({ stopping: taskId, error: null })
    // ⚠ Set when the router stopped the task, even if the re-read after it failed. See `deprovision`.
    let outcome: TaskStopOutcome | null = null
    try {
      outcome = await runTaskStop((name, args) => callTool(name, args), taskId)
      await get().get()
    } catch (error) {
      console.warn(error)
      // ⚠ The router's message, unchanged: it names the refusal (a role, a missing task).
      set({ error: errorMessage(error) })
    } finally {
      set({ stopping: null })
    }
    return outcome
  },

  /**
   * Move a running task's deadline (router#1216).
   *
   * ⚠⚠ `max_hours` IS AN INSTANT FROM NOW, NOT AN AMOUNT ADDED, and it is passed
   * through UNCHANGED. The temptation is to add it to the time remaining so a
   * menu can say "+4 hours" — do not: `deadline_at` is the router's to resolve,
   * this store's copy is up to one poll stale, and the arithmetic would be a
   * second answer to a question the router already answers exactly. The menu
   * states the resulting instant instead.
   *
   * ⚠ `hours === null` OMITS the field, which is what "my plan's default" means
   * on the API. Sending 0 would be a different call entirely — it REMOVES the
   * deadline — so the two must not collapse into one falsy check.
   */
  setDeadline: async (taskId: string, hours: number | null) => {
    set({ deadlineChanging: taskId, error: null })
    // ⚠ True when the router moved the deadline, even if the re-read after it failed. See `deprovision`.
    let done = false
    try {
      await callTool('task_deadline_set', {
        task_id: taskId,
        ...(hours === null ? {} : { max_hours: hours }),
      })
      done = true
      await get().get()
    } catch (error) {
      console.warn(error)
      // The router's own message names the plan on a refusal it cannot grant,
      // which is the one thing a caller needs and the app cannot know.
      set({ error: errorMessage(error) })
    } finally {
      set({ deadlineChanging: null })
    }
    return done
  },

  reset: () => set({ ...state }),
}))

export default useTasks
